import { useMemo, useState, type CSSProperties } from 'react';

import { useAgentId } from '../lib/use-auth.js';
import { useI18n } from '../lib/use-i18n.js';
import { usePlatformBrand } from '../lib/use-platform-brand.js';
import { useShare, type ShareOutcome } from '../lib/use-share.js';
import { useTrackedSnapshot } from '../lib/use-tracked-snapshot.js';
import { leaderboardRepoFor } from '../lib/repositories/leaderboard-repo.js';
import { currentWeekKey, findOwnRank, TIER_LABEL_KEYS } from '../lib/leaderboard-view.js';
import { cohortStanding, personalBest } from '@kilat/shared';
import type { AgentLeaderboard, AgentLeaderboardEntry } from '@kilat/shared';
import { Skeleton as UiSkeleton } from '@kilat/ui';

/** Radius tokens, hoisted out of JSX so `no-literal-string` sees no literal. */
const RADIUS_MD = 'var(--radius-md)';

/**
 * Weekly agent leaderboard tile for the agent /home.
 *
 * Reads ONE bounded doc — `leaderboards/{brandId}/weeks/{currentWeekKey}` — via
 * the tracked-snapshot pattern (no scan; the trigger keeps `entries` sorted +
 * capped server-side). Shows the top slice, highlights the signed-in agent's
 * own row, and — if the agent isn't in the shown slice — still surfaces their
 * standing ("not ranked yet" / a summary chip) so there's no dead-end.
 *
 * Every state has copy ([[feedback-no-dead-ends]]): loading skeleton, empty,
 * error + retry, and populated. All text is keyed via `useI18n()`; the brand
 * name comes from `usePlatformBrand()` (no brand literal).
 */

/** Top slice shown inline (the doc itself retains up to LEADERBOARD_TOP_N). */
const VISIBLE_ROWS = 10;

export function LeaderboardTile() {
  const t = useI18n();
  const agentId = useAgentId();
  const brand = usePlatformBrand();
  const { outcome, share } = useShare();
  const brandId = brand.id;

  // Current ISO week (brand market tz), memoised so the doc id — and thus the
  // subscription key — is stable across renders within the same week.
  const weekKey = useMemo(() => currentWeekKey(), []);
  const [error, setError] = useState<Error | null>(null);
  const [retryNonce, setRetryNonce] = useState(0);

  const subscribe = useMemo(() => {
    const repo = leaderboardRepoFor(brandId);
    return (cb: (v: AgentLeaderboard | null) => void) =>
      repo.snapshot(
        weekKey,
        (v) => {
          setError(null);
          cb(v);
        },
        (err) => setError(err),
      );
    // retryNonce forces a fresh subscription after a retry.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [brandId, weekKey, retryNonce]);

  const { data, loading } = useTrackedSnapshot<AgentLeaderboard | null>(
    ['agent-leaderboard', brandId, weekKey, retryNonce],
    subscribe,
  );

  const entries = useMemo(() => data?.entries ?? [], [data]);
  const own = useMemo(() => findOwnRank(entries, agentId), [entries, agentId]);
  const visible = entries.slice(0, VISIBLE_ROWS);
  const ownShown = own !== null && own.rank <= VISIBLE_ROWS;

  /**
   * Personal best — the agent measured against their own history.
   *
   * Computed for EVERY agent, including the one placed last, because a global
   * rank tells them only that they are losing. `history` comes from the weekly
   * rollup the tile already reads; an agent with no prior weeks gets an honest
   * best-so-far rather than a blank.
   */
  const best = useMemo(() => {
    const current = own?.entry.signupCount ?? 0;
    // `bestSignupCount` is the running maximum the writer carries forward, so
    // the tile never has to read every past week to know the record.
    const prior = own?.entry.bestSignupCount;
    return personalBest(current, prior === undefined ? [] : [prior]);
  }, [own]);

  /**
   * Cohort standing — ranked among agents who started the same month, which is
   * a race a new agent can actually win. Only shown when the cohort is large
   * enough that a position carries information.
   */
  const cohort = useMemo(() => {
    if (!agentId || !own?.entry.cohortMonth) return null;
    const peers = entries.filter((e) => e.cohortMonth === own.entry.cohortMonth);
    const standing = cohortStanding(agentId, own.entry.cohortMonth, peers);
    return standing && !standing.tooSmallToRank ? standing : null;
  }, [agentId, own, entries]);

  const retry = () => {
    setError(null);
    setRetryNonce((n) => n + 1);
  };

  const shareRank = () => {
    if (!own) return;
    void share({
      title: t.t('agent.leaderboard.shareTitle', { brand: brand.name }),
      text: t.t('agent.leaderboard.shareText', { brand: brand.name, rank: own.rank }),
      url: `https://${brand.domains.marketing}/become-an-agent`,
    });
  };

  return (
    <section style={cardStyle}>
      <header style={cardHeaderRow}>
        <h2 style={cardTitle}>{t.t('agent.leaderboard.title')}</h2>
        <span style={cardHint}>{t.t('agent.leaderboard.resetsHint')}</span>
      </header>
      <p style={subtitleStyle}>{t.t('agent.leaderboard.subtitle', { week: weekKey })}</p>

      {error ? (
        <ErrorState onRetry={retry} />
      ) : loading ? (
        <SkeletonRows />
      ) : visible.length === 0 ? (
        <p style={emptyStyle}>{t.t('agent.leaderboard.empty')}</p>
      ) : (
        <>
          <ol style={listStyle}>
            {visible.map((entry, idx) => (
              <LeaderboardRow
                key={entry.agentId}
                entry={entry}
                rank={idx + 1}
                isOwn={agentId !== null && entry.agentId === agentId}
              />
            ))}
          </ol>

          {/* Standing summary so the agent never dead-ends wondering where they
              are: ranked-but-below-the-slice → a chip; unranked → a nudge. */}
          {/* Personal best first: for most agents it is the only line here that
              describes something they can beat this week. */}
          {own ? (
            <p style={ownSummaryStyle}>
              {best.isBest
                ? t.t('agent.leaderboard.personalBestHit', { count: best.current })
                : t.t('agent.leaderboard.personalBestGap', {
                    best: best.best,
                    short: best.shortOfBestBy,
                  })}
            </p>
          ) : null}

          {cohort ? (
            <p style={ownSummaryStyle}>
              {t.t('agent.leaderboard.cohortStanding', {
                rank: cohort.rank,
                size: cohort.cohortSize,
                month: cohort.cohortMonth,
              })}
            </p>
          ) : null}

          {own && !ownShown ? (
            <p style={ownSummaryStyle}>
              {t.t('agent.leaderboard.yourRankSummary', { rank: own.rank })}
            </p>
          ) : null}
          {!own ? <p style={emptyStyle}>{t.t('agent.leaderboard.notRanked')}</p> : null}

          {own ? (
            <div style={shareRowStyle}>
              <button type="button" onClick={shareRank} style={shareButtonStyle}>
                {t.t('agent.leaderboard.shareRank')}
              </button>
              <ShareNote outcome={outcome} />
            </div>
          ) : null}
        </>
      )}
    </section>
  );
}

// ── rows ──────────────────────────────────────────────────────────────────

function LeaderboardRow({
  entry,
  rank,
  isOwn,
}: {
  readonly entry: AgentLeaderboardEntry;
  readonly rank: number;
  readonly isOwn: boolean;
}) {
  const t = useI18n();
  return (
    <li style={isOwn ? { ...rowStyle, ...rowOwnStyle } : rowStyle}>
      <span style={rankStyle}>{t.t('agent.leaderboard.rank', { rank })}</span>
      <span style={nameCellStyle}>
        <span style={nameTextStyle}>{entry.name}</span>
        {isOwn ? <span style={youChipStyle}>{t.t('agent.leaderboard.you')}</span> : null}
        <span style={tierChipStyle}>{t.t(TIER_LABEL_KEYS[entry.tier])}</span>
      </span>
      <span
        style={countStyle}
        aria-label={t.t('agent.leaderboard.signupsAria', { count: entry.signupCount })}
      >
        {entry.signupCount}
      </span>
    </li>
  );
}

function ErrorState({ onRetry }: { readonly onRetry: () => void }) {
  const t = useI18n();
  return (
    <div style={{ display: 'grid', gap: 'var(--space-2)' }}>
      <p style={{ ...emptyStyle, fontWeight: 600, color: 'var(--color-on-surface)' }}>
        {t.t('agent.leaderboard.errorTitle')}
      </p>
      <p style={emptyStyle}>{t.t('agent.leaderboard.errorBody')}</p>
      <div>
        <button type="button" onClick={onRetry} style={shareButtonStyle}>
          {t.t('agent.leaderboard.retry')}
        </button>
      </div>
    </div>
  );
}

function SkeletonRows() {
  const t = useI18n();
  return (
    <div
      role="status"
      aria-busy="true"
      aria-label={t.t('agent.leaderboard.loadingLabel')}
      style={{ display: 'grid', gap: 'var(--space-2)' }}
    >
      {/* Shared shimmer, not a static block — see the note in routes/profile.tsx. */}
      <UiSkeleton count={3} height={'2.75rem'} radius={RADIUS_MD} />
    </div>
  );
}

function ShareNote({ outcome }: { readonly outcome: ShareOutcome }) {
  const t = useI18n();
  if (outcome === 'idle' || outcome === 'cancelled' || outcome === 'shared') return null;
  const key = outcome === 'copied' ? 'agent.leaderboard.copied' : 'agent.leaderboard.shareFailed';
  return (
    <span
      role="status"
      style={{
        fontSize: 'var(--text-meta)',
        color: outcome === 'failed' ? 'var(--color-on-surface)' : 'var(--color-primary)',
      }}
    >
      {t.t(key)}
    </span>
  );
}

// ── styles ──────────────────────────────────────────────────────────────

const cardStyle: CSSProperties = {
  background: 'var(--color-surface)',
  borderRadius: 'var(--radius-lg)',
  padding: 'var(--space-5)',
  boxShadow: 'var(--shadow-1)',
  display: 'grid',
  gap: 'var(--space-3)',
};

const cardHeaderRow: CSSProperties = {
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'baseline',
  gap: 'var(--space-2)',
  flexWrap: 'wrap',
};

const cardTitle: CSSProperties = {
  margin: 0,
  fontSize: 'var(--text-body)',
  fontWeight: 600,
};

const cardHint: CSSProperties = {
  fontSize: 'var(--text-meta)',
  color: 'var(--color-on-surface-muted)',
};

const subtitleStyle: CSSProperties = {
  margin: 0,
  fontSize: 'var(--text-meta)',
  color: 'var(--color-on-surface-muted)',
  lineHeight: 1.4,
};

const emptyStyle: CSSProperties = {
  margin: 0,
  fontSize: 'var(--text-meta)',
  color: 'var(--color-on-surface-muted)',
  lineHeight: 1.5,
};

const listStyle: CSSProperties = {
  listStyle: 'none',
  margin: 0,
  padding: 0,
  display: 'grid',
  gap: 'var(--space-2)',
};

const rowStyle: CSSProperties = {
  display: 'grid',
  gridTemplateColumns: 'auto 1fr auto',
  alignItems: 'center',
  gap: 'var(--space-3)',
  padding: 'var(--space-3)',
  borderRadius: 'var(--radius-md)',
  background: 'var(--tint-subtle)',
  border: '1px solid var(--color-border)',
};

const rowOwnStyle: CSSProperties = {
  background: 'rgba(13,122,79,0.08)',
  border: '1px solid rgba(13,122,79,0.24)',
};

const rankStyle: CSSProperties = {
  fontSize: 'var(--text-meta)',
  fontWeight: 700,
  fontVariantNumeric: 'tabular-nums',
  color: 'var(--color-on-surface-muted)',
  minWidth: '2.25em',
};

const nameCellStyle: CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: 'var(--space-2)',
  minWidth: 0,
  flexWrap: 'wrap',
};

const nameTextStyle: CSSProperties = {
  fontWeight: 600,
  fontSize: 'var(--text-meta)',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
};

const tierChipStyle: CSSProperties = {
  padding: '2px 8px',
  borderRadius: 'var(--radius-pill)',
  fontSize: 'var(--text-meta)',
  fontWeight: 500,
  color: 'var(--color-on-surface-muted)',
  border: '1px solid var(--color-border-strong)',
  textTransform: 'capitalize',
  whiteSpace: 'nowrap',
};

const youChipStyle: CSSProperties = {
  padding: '2px 8px',
  borderRadius: 'var(--radius-pill)',
  fontSize: 'var(--text-meta)',
  fontWeight: 600,
  color: 'var(--color-primary-fg)',
  background: 'var(--color-primary)',
  whiteSpace: 'nowrap',
};

const countStyle: CSSProperties = {
  fontSize: 'var(--text-body)',
  fontWeight: 700,
  fontVariantNumeric: 'tabular-nums',
  color: 'var(--color-on-surface)',
};

const ownSummaryStyle: CSSProperties = {
  margin: 0,
  fontSize: 'var(--text-meta)',
  fontWeight: 600,
  color: 'var(--color-primary)',
};

const shareRowStyle: CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: 'var(--space-3)',
  flexWrap: 'wrap',
};

const shareButtonStyle: CSSProperties = {
  display: 'inline-flex',
  alignItems: 'center',
  padding: '0.5em 1em',
  borderRadius: 'var(--radius-pill)',
  background: 'transparent',
  color: 'var(--color-on-surface)',
  textDecoration: 'none',
  fontWeight: 500,
  fontSize: 'var(--text-meta)',
  border: '1px solid var(--color-border-strong)',
  minHeight: 'var(--touch-target)',
  cursor: 'pointer',
};
